/**
 * Activity API
 * Personal and team activity metrics
 */

import { apiFetch } from './config';

export interface PersonalActivity {
  email_address: string;
  display_name?: string;
  sessions: {
    last_30_days: number;
    last_7_days: number;
    last_session: string | null;
    avg_duration_minutes: number | null;
  };
  commits: {
    last_30_days: number;
    last_7_days: number;
    last_commit: string | null;
    days_since_commit: number | null;
  };
  pull_requests: {
    opened_30d: number;
    merged_30d: number;
    avg_review_hours: string | null;
  };
  value_correlation: {
    commits_within_24h_of_session: number;
    session_to_commit_conversion_pct: number;
  };
}

export interface TeamDeveloper {
  email_address: string;
  display_name: string;
  sessions: { last_30_days: number; last_7_days: number; last_session: string | null };
  commits: { last_30_days: number; last_7_days: number; last_commit: string | null; days_since_commit: number | null };
  prs_merged_30d: number;
  session_to_commit_conversion_pct: number;
  status: 'active' | 'no_ai_usage' | 'low_output' | 'stale' | 'very_stale';
}

export interface TeamSummary {
  total_developers: number;
  active_developers: number;
  stale_developers: number;
  very_stale_developers: number;
  avg_sessions_30d: number;
  avg_commits_30d: number;
  avg_conversion_pct: number;
}

export interface TeamActivity {
  company_id: string;
  summary: TeamSummary;
  developers: TeamDeveloper[];
}

export const activityApi = {
  /**
   * Get current user's activity summary
   */
  getMyActivity: async (): Promise<{ success: boolean; data?: PersonalActivity; error?: string }> => {
    const result = await apiFetch<any>('/api/activity/me');
    if (result.success && result.data) {
      // Unwrap Records envelope
      const records = result.data?.Records || result.data?.data?.Records;
      if (records && records.length > 0) {
        return { success: true, data: records[0] };
      }
      return { success: true, data: result.data };
    }
    return result;
  },

  /**
   * Get team activity (manager view)
   */
  getTeamActivity: async (companyId?: string): Promise<{ success: boolean; data?: TeamActivity; error?: string }> => {
    const params = companyId ? `?company_id=${companyId}` : '';
    const result = await apiFetch<any>(`/api/activity/team${params}`);
    if (result.success && result.data) {
      const records = result.data?.Records || result.data?.data?.Records;
      if (records && records.length > 0) {
        return { success: true, data: records[0] };
      }
      return { success: true, data: result.data };
    }
    return result;
  },
};
